import Vue from 'vue';
import { getContentArea } from '@/contentful/index';
import {
    dateFormat,
    dateFormatYYYYMMDD,
    PdfMake,
  } from '@/constants';
import store from '@/store';
import { isNil, mapKeys } from 'lodash';
import {
    MemberAccountResponseModel,
    PdfMakeTableCell,
    PdfMakeTableCellStyles,
    TableHeader,
    PostedTransactionResponseModel,
  } from '@/@types/member';
import moment from 'moment';
import GeneratePDFService from '@/services/GeneratePDF.service';

interface Data {
    APIDateFormat: string;
    pdfData: PdfMakeTableCell[][];
    pdfLogo: string;
    reportStartDate: string;
    reportEndDate: string;
    claimsData: PdfMakeTableCell[][];
    pdfTagline: string;
}
export default Vue.extend({
    data(): Data {
        return {
          APIDateFormat: dateFormatYYYYMMDD,
          pdfData: [],
          pdfLogo: '',
          reportStartDate: '',
          reportEndDate: '',
          claimsData: [],
          pdfTagline: '',
        };
      },
    beforeMount() {
        // Generate base64 logo for PDF generation
        const CANVAS = document.createElement('CANVAS') as HTMLCanvasElement;
        const CTX: CanvasRenderingContext2D = CANVAS.getContext('2d')!;
        const IMG = document.createElement('img');
        try {
          // use pdf-specific logo if present
          IMG.src = require('@/further-branding-module/static/logo_pdf.svg');
        } catch (error) {
          // fall back to default logo used elsewhere
          IMG.src = require('@/further-branding-module/static/logo_dark.svg');
        }
        IMG.onload = () => {
            CANVAS.width = IMG.width;
            CANVAS.height = IMG.height;
            CTX.drawImage(IMG, 0, 0);
            this.pdfLogo = CANVAS.toDataURL();
        };
        this.getTagline();
    },
    computed: {
        account(): MemberAccountResponseModel {
          return this.$store.getters.currentAccount(this.$route.params.id);
        },
        accountKey(): string {
          return this.$route.params.id;
        },
        postedTransactions(): PostedTransactionResponseModel[] {
            return this.$store.getters.postedTransactions;
          },
        postedTransactionHeaders(): TableHeader[] {
        return [
            {
            text: this.$options.filters!.t('pdf-transactions-date'),
            value: 'postedDate',
            align: 'left',
            },
            {
            text: this.$options.filters!.t('pdf-transactions-description'),
            value: 'description',
            align: 'left',
            },
            {
            text: this.$options.filters!.t('pdf-transactions-type'),
            value: 'transactionType',
            align: 'right',
            },
            {
            text: this.$options.filters!.t('pdf-general-amount'),
            value: 'paymentAmount',
            align: 'right',
            },
        ];
        },

    },
    methods: {
        generatePdfTableHeaders(headers: TableHeader[], customStyles: PdfMakeTableCellStyles = {}): PdfMakeTableCell[] {
            return headers.map((item: any): PdfMakeTableCell => {
              return {
                ...PdfMake.TABLE_HEADER,
                text: item.text,
                ...customStyles,
                alignment: item.text.toLowerCase().includes('amount') ? 'right' : 'left',
              };
            });
          },
          generatePdfTableCells(items: any[], customStyles: PdfMakeTableCellStyles = {}): PdfMakeTableCell[][] {
            return items.reduce((cells: any[], item: any) => {
              cells.push(Object.values(item).map((cellValue): PdfMakeTableCell => {
                const TEXT = !isNil(cellValue) ? cellValue.toString() : '';
                const IS_AMOUNT_FIELD = TEXT && (TEXT.startsWith('$') || TEXT.startsWith('+') || TEXT.startsWith('-')) ;
                return {
                  ...PdfMake.TABLE_CELL,
                  text: TEXT,
                  ...(IS_AMOUNT_FIELD ? {
                    alignment: 'right',
                  } : {}),
                  ...customStyles,
                };
              }));
              return cells;
            }, []);
          },
          async getPostedTransactionsDataForPdf(): Promise<any> {
            const TRANSACTIONS = this.postedTransactions.map((transaction: PostedTransactionResponseModel) => {
              return {
                postedDate: transaction.postedDate ? this.$options.filters!.moment(transaction.postedDate, dateFormat) : '',
                description: transaction.description,
                transactionType: transaction.transactionType,
                paymentAmount: this.$options.filters.transactionFormat(this.$options.filters.currency(transaction.paymentAmount)),
              };
            });
            const headerCellStyles = {
                fontSize: 8.5,
                fillColor: '#fff',
                bold: true,
                nowrap: true,
                borderColor: ['#fff', '#fff', '#fff', '#fff'],
              };
            const cellStyles = {
            fontSize: 8.5,
            };
            const TRANSACTIONS_CELLS = this.generatePdfTableCells(TRANSACTIONS, cellStyles);
            TRANSACTIONS_CELLS.unshift(this.generatePdfTableHeaders(this.postedTransactionHeaders, headerCellStyles));
            this.pdfData = TRANSACTIONS_CELLS;
          },
          async createPdf(): Promise<any> {
            await this.getPostedTransactionsDataForPdf();
            const user = this.$store.getters.user;
            GeneratePDFService
              .createPdf({
                pageOrientation: 'portrait',
                header: (currentPage, pageCount) => {
                  return [
                    {
                      columns: [
                        {
                          stack: [
                            {
                              image: this.pdfLogo,
                              width: 150,
                              },
                              (currentPage === 1 && this.pdfTagline.length) ?
                                {
                                text: this.pdfTagline,
                                margin: [0, 5, 0, 0],
                                fontSize: 7,
                                } : null,
                            ],
                          margin: [50, 40, 0, 0],
                        },
                        {
                          alignment: 'right',
                          margin: [0, 40, 40, 0],
                          text: moment().format(dateFormat),
                        },
                      ],
                    },
                  ];
                },
                content:
                [
                  {
                    text: this.$options.filters!.t('pdf-transactions-transactions'), // this.selectedActivityTypeFilter or date range?
                    style: 'header',
                  },
                  {
                    columns: [
                      {
                        text: this.$options.filters!.t('pdf-general-participant'),
                        style: 'infoText',
                        width: 100,
                      },
                      {
                        text: `${user.firstName} ${user.lastName} (${user.selectAccountId})`,
                        style: 'infoText',
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: this.$options.filters!.t('pdf-general-account'),
                        style: 'infoText',
                        width: 100,
                      },
                      {
                        text: this.account.description,
                        style: 'infoText',
                      },
                    ],
                  },
                  {
                    columns: [
                      {
                        text: this.$options.filters!.t('pdf-general-available-balance'),
                        style: 'infoText',
                        width: 100,
                      },
                      {
                        text: this.$options.filters.transactionFormat(this.$options.filters.currency(this.account.availableBalance)),
                        style: 'infoText',
                      },
                    ],
                  },
                  {
                    text: this.$options.filters!.t('pdf-transactions-posted-transactions'),
                    style: 'header2',
                    margin: [0, 40, 0, 0],
                  },
                  this.pdfData.length > 1 ? {
                    columns: [
                      {
                        style: 'defaultTable',
                        table: {
                          //   headerRows: 1,
                          dontBreakRows: true,
                          body: this.pdfData,
                        widths: ['auto', '*', 'auto', 'auto'],
                        alignments: ['right', 'right', 'right', 'right'],
                        },
                        margin: [0, 10, 0, 10],
                        layout: PdfMake.TABLE_LAYOUT,
                      },
                    ],
                  } : null,
                  this.pdfData.length === 1 ?
                    {
                      text: this.$options.filters!.t('pdf-transactions-no-transactions-found'),
                      style: 'infoText',
                      margin: [0, 10, 0, 10],
                    } : null,
                ],
                footer: (currentPage, pageCount) => {
                  return [
                    {
                      text: currentPage,
                      fontSize: 10,
                      alignment: 'right',
                      margin: [0, 10, 40, 0],
                    },
                  ];
                },
                styles: {
                  defaultTable: {
                    margin: [0, 0, 0, 20],
                    alignment: 'left',
                  },
                  header: {
                    bold: true,
                    fontSize: 15,
                    margin: [0, 30, 0, 5],
                  },
                  header2: {
                    bold: true,
                    fontSize: 12,
                    margin: [0, 10, 0, 5],
                    decoration: 'underline',
                  },
                  infoText: {
                    margin: [0, 3, 0, 3],
                  },
                },
                defaultStyle: {
                    fontSize: 8.5,
                    color: '#222',
                    lineHeight: 1.3,
                },
                pageMargins: [ 50, 100, 50, 40 ],
                pageSize: 'LETTER',
              },
              'activity');
          },
          getTagline(): void {
              getContentArea('pdf-tagline')
                  .then((data) => {
                  if (data) {
                      this.pdfTagline = data.fields.content;
                  }
              });
          },
    },
});

